import type { Metric } from '../economy.schema'
import { ITEM_IDS } from '../items.schema'
import type { ItemId } from '../items.schema'
import type { Economy } from './loadEconomy'

/** One runner's race, as the server counted it (D-038). */
export interface MatchStats {
  /** Final position 1..4. */
  placement: number
  finished: boolean
  /** Shards picked up (one shard = one coin). */
  coins: number
  itemsUsed: number
  itemsByKind: Record<ItemId, number>
  /** Item boxes opened. */
  crates: number
  respawns: number
  /** Hits landed: `hit` events this runner caused on another runner (D-053); not shield pops. */
  hits: number
}

export interface MatchRewards {
  xp: number
  coins: number
}

/** All-zero item counts, one key per item id. */
export function emptyItemCounts(): Record<ItemId, number> {
  return Object.fromEntries(ITEM_IDS.map((id) => [id, 0])) as Record<ItemId, number>
}

function byPlacement(table: readonly number[], placement: number): number {
  return table[placement - 1] ?? 0
}

/**
 * Rewards for one race (D-038):
 * `xp = placement + finish + min(coins, coinCap)·perCoin + min(itemsUsed, itemCap)·perItemUsed
 *   + min(hits, hitCap)·perHitLanded`,
 * `coins = placement + coins·perCoin`. A placement outside 1..4 earns no placement part.
 */
export function matchRewards(stats: MatchStats, economy: Economy): MatchRewards {
  const { xp, coins } = economy
  const shards = Math.max(0, stats.coins)
  const items = Math.max(0, stats.itemsUsed)
  const hits = Math.max(0, stats.hits)
  return {
    xp:
      byPlacement(xp.placement, stats.placement) +
      (stats.finished ? xp.finish : 0) +
      Math.min(shards, xp.coinCap) * xp.perCoin +
      Math.min(items, xp.itemCap) * xp.perItemUsed +
      Math.min(hits, xp.hitCap) * xp.perHitLanded,
    coins: byPlacement(coins.placement, stats.placement) + shards * coins.perCoin,
  }
}

/** How much one race adds to each daily-task metric. */
export function taskProgress(stats: MatchStats): Record<Metric, number> {
  const { finished, placement } = stats
  const out: Record<string, number> = {
    races: 1,
    finishes: finished ? 1 : 0,
    top2: finished && placement >= 1 && placement <= 2 ? 1 : 0,
    wins: finished && placement === 1 ? 1 : 0,
    coins: stats.coins,
    itemsUsed: stats.itemsUsed,
    crates: stats.crates,
    cleanFinishes: finished && stats.respawns === 0 ? 1 : 0,
    hits: stats.hits,
  }
  for (const id of ITEM_IDS) out[`item:${id}`] = stats.itemsByKind[id] ?? 0
  return out
}
